import { useEffect, useState } from 'react';

const LOGO_URL = 'https://static.readdy.ai/image/5b0394d63ea67e242d80d6c69b47f9d5/248fb4068954f3ccbfda9e34faec5a86.png';

interface PreloaderProps {
  onComplete: () => void;
}

export default function Preloader({ onComplete }: PreloaderProps) {
  const [phase, setPhase] = useState<'enter' | 'hold' | 'exit'>('enter');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Animate progress bar from 0 → 100 over 2.2 seconds
    const startTime = performance.now();
    const duration = 2200;

    const animateProgress = (now: number) => {
      const elapsed = now - startTime;
      const pct = Math.min((elapsed / duration) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        requestAnimationFrame(animateProgress);
      }
    };
    requestAnimationFrame(animateProgress);

    // Phase transitions
    const holdTimer = setTimeout(() => setPhase('hold'), 400);
    const exitTimer = setTimeout(() => setPhase('exit'), 2600);
    const doneTimer = setTimeout(() => onComplete(), 3300);

    return () => {
      clearTimeout(holdTimer);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [onComplete]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
      style={{
        backgroundColor: 'oklch(0.14 0.10 264)',
        opacity: phase === 'exit' ? 0 : 1,
        visibility: phase === 'exit' ? 'hidden' : 'visible',
        transition: 'opacity 0.7s ease-in-out, visibility 0.7s ease-in-out',
        pointerEvents: phase === 'exit' ? 'none' : 'auto',
      }}
      aria-label="Loading Happy Faces Hyderabad"
      role="status"
    >
      {/* Background radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, oklch(0.22 0.12 264) 0%, transparent 70%)',
        }}
      ></div>

      {/* Subtle tricolor diagonal streaks */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-5">
        <div className="absolute top-0 left-0 w-full h-1/3 bg-accent-500"></div>
        <div className="absolute top-1/3 left-0 w-full h-1/3 bg-white"></div>
        <div className="absolute top-2/3 left-0 w-full h-1/3 bg-secondary-500"></div>
      </div>

      {/* Main content */}
      <div className="relative flex flex-col items-center gap-8 px-8">

        {/* Logo with animated rings */}
        <div className="relative flex items-center justify-center">
          {/* Outer pulse ring 1 */}
          <div
            className="absolute rounded-full border-2 border-accent-500/20"
            style={{
              width: 200,
              height: 200,
              animation: phase !== 'enter' ? 'ringPulse 2s ease-out infinite' : 'none',
            }}
          ></div>
          {/* Outer pulse ring 2 — offset */}
          <div
            className="absolute rounded-full border border-secondary-500/15"
            style={{
              width: 240,
              height: 240,
              animation: phase !== 'enter' ? 'ringPulse 2s ease-out 0.5s infinite' : 'none',
            }}
          ></div>

          {/* Tricolor orbit ring */}
          <div
            className="absolute rounded-full"
            style={{
              width: 164,
              height: 164,
              background: 'conic-gradient(oklch(0.77 0.16 73) 0deg 120deg, white 120deg 240deg, oklch(0.52 0.14 142) 240deg 360deg)',
              animation: phase !== 'enter' ? 'spin 4s linear infinite' : 'none',
              opacity: 0.5,
            }}
          ></div>
          {/* Inner white mask for orbit ring */}
          <div
            className="absolute rounded-full"
            style={{ width: 148, height: 148, backgroundColor: 'oklch(0.14 0.10 264)' }}
          ></div>

          {/* Logo image */}
          <img
            src={LOGO_URL}
            alt="Happy Faces Hyderabad Logo"
            className="relative rounded-full object-cover border-4 border-white/10"
            style={{
              width: 140,
              height: 140,
              animation: 'logoReveal 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
              opacity: 0,
            }}
          />
        </div>

        {/* Organization name */}
        <div className="text-center space-y-1" style={{ animation: 'textSlideUp 0.7s 0.5s ease-out forwards', opacity: 0 }}>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-wide">
            Happy Faces Hyderabad
          </h1>
          <p className="text-accent-400 text-sm font-medium tracking-widest uppercase">
            Rural Development Welfare Society
          </p>
          <p
            className="text-white/40 text-xs italic font-light mt-2"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            &ldquo;Being kind makes the world better&rdquo;
          </p>
        </div>

        {/* Tricolor progress bar */}
        <div className="w-72 md:w-96" style={{ animation: 'textSlideUp 0.5s 0.8s ease-out forwards', opacity: 0 }}>
          <div className="w-full h-1.5 rounded-full overflow-hidden bg-white/10">
            {/* Saffron segment */}
            <div className="h-full flex rounded-full overflow-hidden">
              <div
                className="h-full bg-accent-500 rounded-l-full transition-none"
                style={{ width: `${Math.min(progress, 33.3)}%`, transition: 'width 0.05s linear' }}
              ></div>
              <div
                className="h-full bg-white/80 transition-none"
                style={{ width: `${Math.max(0, Math.min(progress - 33.3, 33.3))}%`, transition: 'width 0.05s linear' }}
              ></div>
              <div
                className="h-full bg-secondary-500 rounded-r-full transition-none"
                style={{ width: `${Math.max(0, Math.min(progress - 66.6, 33.4))}%`, transition: 'width 0.05s linear' }}
              ></div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-2">
            <p className="text-white/30 text-xs">Initializing…</p>
            <p className="text-white/40 text-xs font-mono">{Math.round(progress)}%</p>
          </div>
        </div>

        {/* Activity dots */}
        <div
          className="flex items-center gap-2"
          style={{ animation: 'textSlideUp 0.5s 1s ease-out forwards', opacity: 0 }}
        >
          {['Food Distribution', 'Education', 'Healthcare', 'Women Empowerment'].map((label, i) => (
            <div
              key={label}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10"
              style={{ animation: `fadeIn 0.4s ${0.9 + i * 0.1}s ease-out forwards`, opacity: 0 }}
            >
              <div
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  backgroundColor: i === 0
                    ? 'oklch(0.77 0.16 73)'
                    : i === 1
                    ? 'oklch(0.52 0.14 142)'
                    : i === 2
                    ? 'white'
                    : 'oklch(0.77 0.16 73)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  animationDelay: `${i * 0.3}s`,
                }}
              ></div>
              <span className="text-white/40 text-[10px] whitespace-nowrap hidden sm:block">{label}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}